import React, { useCallback, useEffect, useState } from 'react';
import { adminAPI } from '@/services/api';
import { MessageSquare, Search, CheckCircle2, XCircle, Clock, X, Eye } from 'lucide-react';
import Pagination from '@/components/shared/Pagination';
import ScrollHint from '@/components/shared/ScrollHint';

const STATUS_META = {
  pending:   { label: 'Pending',   cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',   icon: Clock },
  ignored:   { label: 'Ignored',   cls: 'bg-zinc-500/10 text-slate-500 dark:text-zinc-400 border-zinc-500/20',       icon: XCircle },
  completed: { label: 'Completed', cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20', icon: CheckCircle2 },
};

const CATEGORY_META = {
  bug:              { label: 'Bug',            cls: 'bg-red-500/10 text-red-600 dark:text-red-400' },
  feature_request:  { label: 'Feature request', cls: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  complaint:        { label: 'Complaint',      cls: 'bg-orange-500/10 text-orange-600 dark:text-orange-400' },
  praise:           { label: 'Praise',         cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  suggestion:       { label: 'Suggestion',     cls: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${meta.cls}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

function CategoryPill({ category }) {
  const meta = CATEGORY_META[category] || { label: category || '-', cls: 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400' };
  return <span className={`text-[10px] px-2 py-0.5 rounded ${meta.cls}`}>{meta.label}</span>;
}

function DetailModal({ feedback, onClose, onUpdateStatus, updating }) {
  if (!feedback) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="feedback-detail-modal">
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-zinc-800 flex items-center justify-between">
          <div>
            <h2 className="text-slate-900 dark:text-white font-semibold text-lg">Feedback from {feedback.name || feedback.email}</h2>
            <p className="text-slate-500 dark:text-zinc-500 text-xs mt-0.5">{feedback.email}</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white rounded" data-testid="close-feedback-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 flex flex-wrap gap-2 border-b border-slate-200 dark:border-zinc-800/60">
          <StatusPill status={feedback.status} />
          <CategoryPill category={feedback.category} />
          <span className="text-[11px] text-slate-500 dark:text-zinc-500">
            Submitted {feedback.created_at ? new Date(feedback.created_at).toLocaleString() : '-'}
          </span>
        </div>

        <div className="px-5 py-4 overflow-auto flex-1">
          {feedback.summary && (
            <p className="text-xs text-slate-500 dark:text-zinc-500 uppercase tracking-wider mb-2">Summary</p>
          )}
          {feedback.summary && (
            <p className="text-sm text-slate-600 dark:text-zinc-300 mb-4 italic">&ldquo;{feedback.summary}&rdquo;</p>
          )}
          <p className="text-xs text-slate-500 dark:text-zinc-500 uppercase tracking-wider mb-2">Full feedback</p>
          <p className="text-sm text-slate-700 dark:text-zinc-200 whitespace-pre-wrap leading-relaxed" data-testid="feedback-full-text">
            {feedback.message}
          </p>
        </div>

        <div className="px-5 py-4 border-t border-slate-200 dark:border-zinc-800 flex flex-wrap gap-2 justify-end">
          <button
            disabled={updating || feedback.status === 'pending'}
            onClick={() => onUpdateStatus(feedback.id, 'pending')}
            className="px-3 py-1.5 text-xs rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 disabled:opacity-40"
            data-testid="mark-pending-btn"
          >Mark Pending</button>
          <button
            disabled={updating || feedback.status === 'ignored'}
            onClick={() => onUpdateStatus(feedback.id, 'ignored')}
            className="px-3 py-1.5 text-xs rounded-lg bg-slate-100 dark:bg-zinc-800 border border-slate-300 dark:border-zinc-700 text-slate-600 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-700 disabled:opacity-40"
            data-testid="mark-ignored-btn"
          >Mark Ignored</button>
          <button
            disabled={updating || feedback.status === 'completed'}
            onClick={() => onUpdateStatus(feedback.id, 'completed')}
            className="px-3 py-1.5 text-xs rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 disabled:opacity-40"
            data-testid="mark-completed-btn"
          >Mark Completed</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, ignored: 0, completed: 0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (p = page, ps = pageSize) => {
    setLoading(true);
    setError('');
    try {
      const data = await adminAPI.listFeedbacks({ page: p, limit: ps, status: statusFilter, search });
      setItems(data.feedbacks || []);
      setTotal(data.total || 0);
      setCounts(data.counts || { pending: 0, ignored: 0, completed: 0 });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load feedbacks');
    }
    setLoading(false);
  }, [page, pageSize, statusFilter, search]);

  useEffect(() => { setPage(1); load(1, pageSize);}, [statusFilter, search, pageSize]);
  useEffect(() => { load(page, pageSize);}, [page]);

  const submitSearch = (e) => { e.preventDefault(); setSearch(searchInput.trim()); };

  const updateStatus = async (id, status) => {
    setUpdating(true);
    try {
      const updated = await adminAPI.updateFeedbackStatus(id, status);
      setItems(prev => prev.map(x => x.id === id ? updated : x));
      if (selected && selected.id === id) setSelected(updated);
      // refresh counts
      load(page);
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
    setUpdating(false);
  };

  return (
    <div data-testid="admin-feedback-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">User Feedback</h1>
          <p className="text-slate-500 dark:text-zinc-500 text-sm">
            Captured automatically by Jay across the platform · {total} total
          </p>
        </div>
      </div>

      {/* Counter chips */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {['pending', 'ignored', 'completed'].map(k => {
          const meta = STATUS_META[k];
          const Icon = meta.icon;
          const active = statusFilter === k;
          return (
            <button
              key={k}
              onClick={() => setStatusFilter(active ? '' : k)}
              className={`text-left p-4 rounded-xl border transition-all ${active ? meta.cls + ' border' : 'bg-white dark:bg-[#111114] border-slate-200 dark:border-zinc-800/50 hover:border-slate-300 dark:hover:border-zinc-700'}`}
              data-testid={`filter-${k}-btn`}
            >
              <div className={`flex items-center gap-2 text-xs uppercase tracking-wider ${active ? '' : 'text-slate-500 dark:text-zinc-500'}`}>
                <Icon className="w-3.5 h-3.5" />
                {meta.label}
              </div>
              <div className={`text-2xl font-semibold mt-1 ${active ? '' : 'text-slate-900 dark:text-white'}`}>{counts[k] || 0}</div>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <form onSubmit={submitSearch} className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-500" />
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search by name or email..."
              className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 w-72"
              data-testid="feedback-search-input"
            />
          </div>
          <button type="submit" className="px-3 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm hover:bg-slate-200 dark:hover:bg-zinc-700" data-testid="feedback-search-btn">Search</button>
          {(search || statusFilter) && (
            <button
              type="button"
              onClick={() => { setSearch(''); setSearchInput(''); setStatusFilter(''); }}
              className="px-3 py-2 text-sm text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
              data-testid="clear-filters-btn"
            >Clear</button>
          )}
        </form>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-600 dark:text-zinc-300"
          data-testid="feedback-status-filter"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="ignored">Ignored</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 rounded-lg px-4 py-2 mb-3 text-sm">{error}</div>
      )}

      {loading ? (
        <p className="text-slate-500 dark:text-zinc-500">Loading...</p>
      ) : items.length === 0 ? (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-10 text-center" data-testid="feedback-empty-state">
          <MessageSquare className="w-10 h-10 text-slate-600 dark:text-zinc-700 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-zinc-500">No feedback found</p>
          <p className="text-slate-500 dark:text-zinc-600 text-xs mt-1">User messages to Jay are auto-classified &mdash; they&apos;ll show up here when detected.</p>
        </div>
      ) : (
        <>
          <ScrollHint>
          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-slate-200 dark:border-zinc-800/50">
                  <th className="text-left px-4 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Name</th>
                  <th className="text-left px-4 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Email</th>
                  <th className="text-left px-4 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Feedback</th>
                  <th className="text-left px-4 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Category</th>
                  <th className="text-left px-4 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Status</th>
                  <th className="text-left px-4 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Submitted</th>
                  <th className="text-right px-4 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(fb => (
                  <tr key={fb.id} className="border-b border-slate-200 dark:border-zinc-800/30 last:border-0 hover:bg-slate-100 dark:hover:bg-zinc-800/20" data-testid={`feedback-row-${fb.id}`}>
                    <td className="px-4 py-3 text-sm text-slate-700 dark:text-zinc-200">{fb.name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-500 dark:text-zinc-400">{fb.email}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-zinc-300 max-w-md">
                      <span title={fb.message}>
                        {(fb.message || '').slice(0, 90)}{(fb.message || '').length > 90 ? '…' : ''}
                      </span>
                    </td>
                    <td className="px-4 py-3"><CategoryPill category={fb.category} /></td>
                    <td className="px-4 py-3"><StatusPill status={fb.status} /></td>
                    <td className="px-4 py-3 text-xs text-slate-500 dark:text-zinc-500">
                      {fb.created_at ? new Date(fb.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setSelected(fb)}
                          className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10 rounded"
                          title="View full feedback"
                          data-testid={`view-feedback-${fb.id}`}
                        ><Eye className="w-4 h-4" /></button>
                        {fb.status !== 'completed' && (
                          <button
                            onClick={() => updateStatus(fb.id, 'completed')}
                            disabled={updating}
                            className="p-1.5 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 rounded"
                            title="Mark completed"
                            data-testid={`complete-feedback-${fb.id}`}
                          ><CheckCircle2 className="w-4 h-4" /></button>
                        )}
                        {fb.status !== 'ignored' && (
                          <button
                            onClick={() => updateStatus(fb.id, 'ignored')}
                            disabled={updating}
                            className="p-1.5 text-slate-500 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-zinc-700 rounded"
                            title="Mark ignored"
                            data-testid={`ignore-feedback-${fb.id}`}
                          ><XCircle className="w-4 h-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </ScrollHint>

          {total > pageSize && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              testIdPrefix="admin-feedback-pg"
            />
          )}
        </>
      )}

      <DetailModal
        feedback={selected}
        onClose={() => setSelected(null)}
        onUpdateStatus={updateStatus}
        updating={updating}
      />
    </div>
  );
}
